import { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip } from "recharts";
import { useBodyMeasurements } from "@/hooks/use-body-measurements";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";
import { computeTrendWeight } from "@/lib/recovery/trend";
import { SkeletonLoader, EmptyState } from "@/components/system";

/**
 * 90-day weight trend (SPEC 3.3.4). Reads body_measurement (weight_kg
 * not null), plots one point per measurement. Goal line reads from
 * athlete_profile.goal_weight_kg.
 *
 * v1 chart is intentionally minimal — no smoothing, no body-fat overlay.
 * Recharts handles ResponsiveContainer for layout.
 */

export function WeightTrendChart() {
  const measurements = useBodyMeasurements({ lookbackDays: 90, limit: 200 });
  const profile = useAthleteProfile();
  const [showRaw, setShowRaw] = useState(false);

  const data = useMemo(() => {
    const samples = [...(measurements.data ?? [])]
      .filter((m) => m.weight_kg != null)
      .reverse()
      .map((m) => ({
        date: m.ts.slice(0, 10),
        weight_kg: Number(m.weight_kg),
      }));
    const trended = computeTrendWeight(samples);
    return trended.map((t) => ({
      date: t.date,
      weight: t.weight_kg,
      trend: t.trend_kg,
    }));
  }, [measurements.data]);

  if (measurements.isLoading) return <SkeletonLoader variant="card" height={200} />;
  if (data.length === 0) {
    return (
      <EmptyState
        headline="No weight measurements yet."
        sub="Tap the Weight quick-log button on Today, or wire up Garmin Index S2 / HAE."
      />
    );
  }

  const goalKg = profile.data?.goal_weight_kg
    ? Number(profile.data.goal_weight_kg)
    : null;

  const allValues = data.flatMap((d) => [d.weight, d.trend]);
  const minW = Math.min(...allValues);
  const maxW = Math.max(...allValues);
  const yMin = Math.floor(Math.min(minW, goalKg ?? minW) - 1);
  const yMax = Math.ceil(maxW + 1);

  return (
    <article
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-5)",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: "var(--space-3)",
        }}
      >
        <h3
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            fontSize: "var(--type-body-l)",
            margin: 0,
            color: "var(--fg)",
          }}
        >
          Weight, last 90 days
        </h3>
        <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "baseline" }}>
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--fg-3)",
              fontSize: "var(--type-meta)",
              textDecoration: "underline",
              textDecorationColor: "var(--hair-2)",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {showRaw ? "Hide raw" : "Show raw"}
          </button>
          <p
            style={{
              color: "var(--fg-3)",
              fontSize: "var(--type-meta)",
              margin: 0,
            }}
            className="mono"
          >
            {data.length} measurement{data.length === 1 ? "" : "s"}
          </p>
        </div>
      </header>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="date"
              stroke="var(--fg-4)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
              minTickGap={36}
            />
            <YAxis
              domain={[yMin, yMax]}
              stroke="var(--fg-4)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
              tickFormatter={(v) => `${v}`}
              width={36}
            />
            <Tooltip
              contentStyle={{
                background: "var(--bg-3)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-card)",
                color: "var(--fg)",
                fontSize: "var(--type-body-s)",
              }}
              labelStyle={{ color: "var(--fg-3)" }}
              cursor={{ stroke: "var(--hair-2)" }}
            />
            {goalKg != null && (
              <ReferenceLine
                y={goalKg}
                stroke="var(--accent-dim)"
                strokeDasharray="4 4"
                label={{
                  value: `goal ${goalKg}kg`,
                  position: "right",
                  fill: "var(--accent)",
                  fontSize: 10,
                }}
              />
            )}
            {showRaw && (
              <Line
                type="monotone"
                dataKey="weight"
                stroke="var(--fg-4)"
                strokeWidth={1}
                dot={{ r: 1.5, fill: "var(--fg-4)" }}
                activeDot={{ r: 3 }}
                animationDuration={300}
                name="raw kg"
              />
            )}
            <Line
              type="monotone"
              dataKey="trend"
              stroke="var(--accent)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              animationDuration={300}
              name="trend kg"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}
